// Client-side counterpart of author-api's tagService.isNearMatch — same
// thresholds, kept in sync deliberately so a "did you mean" suggestion here
// matches the near-duplicate advisory the backend would show if this were a
// tag-creation flow (it isn't; this only powers picker suggestions).
const levenshteinDistance = (a: string, b: string): number => {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  let previousRow: number[] = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i += 1) {
    const currentRow: number[] = [i];
    for (let j = 1; j <= b.length; j += 1) {
      const substitutionCost = a[i - 1] === b[j - 1] ? 0 : 1;
      // Fallbacks below are never actually hit — j/j-1 are always in bounds
      // for previousRow/currentRow here — but noUncheckedIndexedAccess can't
      // prove that from the loop shape.
      const deletion = (previousRow[j] ?? Infinity) + 1;
      const insertion = (currentRow[j - 1] ?? Infinity) + 1;
      const substitution = (previousRow[j - 1] ?? Infinity) + substitutionCost;
      currentRow.push(Math.min(deletion, insertion, substitution));
    }
    previousRow = currentRow;
  }
  return previousRow[b.length] ?? Math.max(a.length, b.length);
};

const NEAR_MATCH_SIMILARITY_THRESHOLD = 0.6;
const MIN_CONTAINMENT_LENGTH = 3;

export function isNearMatch(candidateName: string, existingName: string): boolean {
  const a = candidateName.trim().toLowerCase();
  const b = existingName.trim().toLowerCase();
  if (a.length === 0 || b.length === 0 || a === b) return false;
  if (Math.min(a.length, b.length) >= MIN_CONTAINMENT_LENGTH && (a.includes(b) || b.includes(a))) return true;

  const distance = levenshteinDistance(a, b);
  const similarity = 1 - distance / Math.max(a.length, b.length);
  return similarity >= NEAR_MATCH_SIMILARITY_THRESHOLD;
}
